import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCurrency } from 'utils/currencyFormatter';
import { getOrderStatus } from 'constants';
import './PieCharts.scss';

const PieCharts = ({ data }) => {
  const { t } = useTranslation(['backoffice']);

  // Obtener estados traducidos
  const orderStatuses = getOrderStatus(t);
  // Crear un mapa para buscar traducciones por id de estado
  const statusMap = new Map(orderStatuses.map(status => [status.id, status.value]));

  // Transformar datos del endpoint a la estructura esperada
  const transformData = (apiData) => {
    const shipped = apiData?.shipped_books || {};
    const ordered = apiData?.total_ordered_books || {};
    const byStatus = apiData?.total_ordered_books_by_status || [];
    const outOfStock = apiData?.total_ordered_books_out_of_stock || {};

    // Mapear todos los estados del endpoint y traducirlos según el idioma
    const porEstado = byStatus.map(statusItem => {
      const statusId = statusItem.status;
      // Buscar la traducción del estado, si no existe usar el status_display o el status original
      const statusDisplay = statusMap.get(statusId) || statusItem.status_display || statusItem.status;
      
      return {
        status: statusId,
        statusDisplay: statusDisplay,
        articulos: statusItem.articles_count ?? 0,
        valor: statusItem.total_amount ?? 0
      };
    });

    return {
      libros_enviados: {
        importe_definitivo: {
          articulos: shipped.total_shipped_articles ?? 0,
          importe: shipped.final_amount ?? 0
        },
        restante_definitivo: shipped.remaining_final ?? 0
      },
      total_libros_pedidos: {
        importe_provisional: {
          articulos: ordered.total_articles ?? 0,
          importe: ordered.provisional_amount ?? 0
        },
        restante_provisional: ordered.remaining_provisional ?? 0
      },
      por_estado: porEstado,
      descatalogados: {
        descatalogados: { articulos: outOfStock.out_of_stock ?? 0 },
        agotados: { articulos: outOfStock.sold_out ?? 0 },
        restantes: { articulos: outOfStock.remaining ?? 0 }
      }
    };
  };

  // Colores para los gráficos
  const COLORS = {
    orange: '#FFA500',
    lightGreen: '#90EE90',
    lightBlue: '#87CEEB',
    red: '#FF6B6B',
    purple: '#9370DB',
    darkGreen: '#228B22',
    pink: '#FFB6C1'
  };

  // Función para generar un color aleatorio basado en un string
  const generateColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 60%)`;
  };

  const chartData = transformData(data);

  // Preparar datos para "Libros enviados"
  const librosEnviadosData = [
    {
      name: t('statistics.importeDefinitivo', { ns: 'backoffice' }),
      value: chartData.libros_enviados?.importe_definitivo?.importe ?? 0,
      articulos: chartData.libros_enviados?.importe_definitivo?.articulos ?? 0,
      color: COLORS.orange
    },
    {
      name: t('statistics.restanteDefinitivo', { ns: 'backoffice' }),
      value: chartData.libros_enviados?.restante_definitivo ?? 0,
      articulos: 0,
      color: COLORS.lightGreen
    }
  ];

  // Preparar datos para "Total libros pedidos"
  const totalLibrosPedidosData = [
    {
      name: t('statistics.importeProvisional', { ns: 'backoffice' }),
      value: chartData.total_libros_pedidos?.importe_provisional?.importe ?? 0,
      articulos: chartData.total_libros_pedidos?.importe_provisional?.articulos ?? 0,
      color: COLORS.orange
    },
    {
      name: t('statistics.restanteProvisional', { ns: 'backoffice' }),
      value: Math.abs(chartData.total_libros_pedidos?.restante_provisional ?? 0),
      articulos: undefined,
      restanteOriginal: chartData.total_libros_pedidos?.restante_provisional ?? 0,
      color: COLORS.lightGreen
    }
  ];

  // Preparar datos para "Por estado" - usar todos los estados del backend
  const porEstadoData = (chartData.por_estado && Array.isArray(chartData.por_estado) && chartData.por_estado.length > 0)
    ? chartData.por_estado.map(item => ({
        name: item.statusDisplay || item.status,
        value: item.articulos ?? 0,
        valor: formatCurrency(item.valor ?? 0),
        color: generateColorFromString(item.status || item.statusDisplay)
      }))
    : [];

  // Preparar datos para "Descatalogados"
  const descatalogadosData = [
    {
      name: t('statistics.descatalogados', { ns: 'backoffice' }),
      value: chartData.descatalogados?.descatalogados?.articulos || 0,
      color: COLORS.pink
    },
    {
      name: t('statistics.agotados', { ns: 'backoffice' }),
      value: chartData.descatalogados?.agotados?.articulos || 0,
      color: COLORS.lightBlue
    },
    {
      name: t('statistics.restantes', { ns: 'backoffice' }),
      value: chartData.descatalogados?.restantes?.articulos || 0,
      color: COLORS.orange
    }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="pie-chart-tooltip">
          <p>{payload[0].name}</p>
          <p>{payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (data, chartType) => {
    return (
      <div className="pie-chart-legend">
        {data.map((entry, index) => {
          let legendText = entry.name;
          
          if (chartType === 'librosEnviados') {
            if (entry.articulos !== undefined && entry.articulos !== null) {
              // Para el importe definitivo: siempre mostrar artículos (incluso si es 0) e importe
              legendText += `, ${entry.articulos} ${entry.articulos === 1 ? t('statistics.articulo', { ns: 'backoffice' }) : t('statistics.articulos', { ns: 'backoffice' })} (${formatCurrency(entry.value)})`;
            } else {
              // Para el restante: solo mostrar importe (no tiene artículos)
              legendText += ` (-${formatCurrency(Math.abs(entry.value))})`;
            }
          } else if (chartType === 'totalLibrosPedidos') {
            if (entry.articulos !== undefined && entry.articulos !== null) {
              // Para el importe provisional: siempre mostrar artículos (incluso si es 0) e importe
              legendText += `, ${entry.articulos} ${entry.articulos === 1 ? t('statistics.articulo', { ns: 'backoffice' }) : t('statistics.articulos', { ns: 'backoffice' })} (${formatCurrency(entry.value)})`;
            } else {
              // Para el restante: mostrar el valor original (negativo) tal como viene del backend
              const restanteValue = entry.restanteOriginal ?? chartData.total_libros_pedidos?.restante_provisional ?? 0;
              legendText += ` (-${formatCurrency(Math.abs(restanteValue))})`;
            }
          } else if (chartType === 'porEstado') {
            legendText += `, ${entry.articulos || entry.value} ${(entry.articulos || entry.value) === 1 ? t('statistics.articulo', { ns: 'backoffice' }) : t('statistics.articulos', { ns: 'backoffice' })} (${entry.valor})`;
          } else if (chartType === 'descatalogados') {
            legendText += `, ${entry.value} ${entry.value === 1 ? t('statistics.articulo', { ns: 'backoffice' }) : t('statistics.articulos', { ns: 'backoffice' })}`;
          }
          
          return (
            <div key={index} className="pie-chart-legend-item">
              <div
                className="pie-chart-legend-color"
                style={{ backgroundColor: entry.color }}
              ></div>
              <span className="pie-chart-legend-text">{legendText}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="pie-charts-container">
      <Row className="pie-charts-row">
        {/* Gráfico 1: Libros enviados */}
        <Col md={6} className="pie-chart-col">
          <div className="pie-chart-card">
            <h3 className="pie-chart-title">
              {t('statistics.librosEnviados', { ns: 'backoffice' })}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={librosEnviadosData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {librosEnviadosData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {renderLegend(librosEnviadosData.map((entry, idx) => ({
              ...entry,
              articulos: idx === 0 ? entry.articulos : undefined
            })), 'librosEnviados')}
          </div>
        </Col>

        {/* Gráfico 2: Total libros pedidos */}
        <Col md={6} className="pie-chart-col">
          <div className="pie-chart-card">
            <h3 className="pie-chart-title">
              {t('statistics.totalLibrosPedidos', { ns: 'backoffice' })}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={totalLibrosPedidosData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {totalLibrosPedidosData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {renderLegend(totalLibrosPedidosData.map((entry, idx) => ({
              ...entry,
              articulos: idx === 0 ? entry.articulos : undefined
            })), 'totalLibrosPedidos')}
          </div>
        </Col>
      </Row>

      <Row className="pie-charts-row">
        {/* Gráfico 3: Por estado */}
        <Col md={6} className="pie-chart-col">
          <div className="pie-chart-card">
            <h3 className="pie-chart-title">
              {t('statistics.totalLibrosPedidosPorEstado', { ns: 'backoffice' })}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={porEstadoData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {porEstadoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {renderLegend(porEstadoData.map(entry => ({
              ...entry,
              articulos: entry.value
            })), 'porEstado')}
          </div>
        </Col>

        {/* Gráfico 4: Descatalogados */}
        <Col md={6} className="pie-chart-col">
          <div className="pie-chart-card">
            <h3 className="pie-chart-title">
              {t('statistics.totalLibrosPedidosDescatalogados', { ns: 'backoffice' })}
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={descatalogadosData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {descatalogadosData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {renderLegend(descatalogadosData, 'descatalogados')}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PieCharts;
